import React, { useState, useEffect, useCallback } from "react";

import { Grid, IconButton, MenuItem, SelectChangeEvent } from "@mui/material";
import { useTranslation } from "react-i18next";
import { HiChevronDoubleUp, HiOutlineChevronDoubleDown } from "react-icons/hi";

import { useActionQuote } from "../../../../hooks/useActionQuote";
import { Destination } from "../../../../models/destination";
import { DifferentLocation as LocationAddress } from "../../../../models/location";
import { Material } from "../../../../models/material";
import { RoomJob, QuotedItem } from "../../../../models/sales";
import { ITEM_DESTINATION_COLOR } from "../../../../util/enums";
import Select from "../../../Form/Select";
import { useAction } from "../useActions";
import RoomContentItem from "./RoomItemContent";
import useStyles from "./styles";

interface QuoteInventoryListProps {
  data: RoomJob[];
  allData: RoomJob[];
  jobId: number;
  dataForSelectRoom: RoomJob[];
  locations: LocationAddress[];
  destinations: Destination[];
  destination?: Destination;
  viewOnly?: boolean;
  setDestination: (destination: Destination) => void;
  roomIdSelected: string;
  setRoomIdSelected: (value: string) => void;
  update: (value: RoomJob[]) => void;
  onCustomerView?: boolean;
  noHideOnExpand?: boolean;
}

const RoomInfoListSection = ({
  data,
  allData,
  jobId,
  dataForSelectRoom,
  locations,
  destinations,
  viewOnly = false,
  roomIdSelected,
  destination,
  setDestination,
  setRoomIdSelected,
  update,
  onCustomerView = false,
  noHideOnExpand,
}: QuoteInventoryListProps) => {
  const { t: translate } = useTranslation();

  const [expandAll, setExpandAll] = useState<boolean>(false);
  const [idSelected, setIdSelected] = useState<string>("");

  const { onDeleteRoom } = useActionQuote();

  const {
    handleUpdateRoom,
    handleDeleteItem,
    handleUpdateItem,
    handleDeleteMaterial,
    handleUpdateMaterial,
    handleAddPicturesToRoom,
    handleDeletePicturesFromRoom,
  } = useAction({ data: allData, jobId, update });

  const classes = useStyles();

  const handleSetIdSelected = useCallback(
    (roomIdSelected: string) => setIdSelected(roomIdSelected),
    [roomIdSelected]
  );

  useEffect(() => {
    if (!onCustomerView) handleSetIdSelected(roomIdSelected);
  }, [roomIdSelected]);

  useEffect(() => {
    if (!destination && destinations && destinations.length > 0) setDestination(destinations[0]);
  }, [destinations, destination]);

  const renderHeaderRoom = () => {
    return (
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
      >
        <Grid item xs={3}>
          {!noHideOnExpand && (
            <Select
              data-cy="selectRoom"
              name="room"
              label={translate("room")}
              value={idSelected}
              displayEmpty
              onChange={(e: SelectChangeEvent<any>) => {
                setRoomIdSelected(e.target.value);
                if (e.target.value === "") setExpandAll(false);
                else setExpandAll(true);
              }}
            >
              <MenuItem value="">{translate("summary")}</MenuItem>
              {dataForSelectRoom.map((item) => (
                <MenuItem data-cy={`${item.name}-room`} key={item.name} value={item.name}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          )}
        </Grid>
        {!viewOnly && (
          <Grid item xs={6}>
            <Select
              data-cy="selectDestination"
              style={{
                color: destination?.color || ITEM_DESTINATION_COLOR.None,
              }}
              name="destination"
              label={translate("destination")}
              displayEmpty
              disabled={viewOnly}
              value={destination?.name || ""}
              onChange={(event: SelectChangeEvent<any>) => {
                const destination = destinations.find(
                  (destination) => destination.name === event.target.value
                );

                if (destination) setDestination(destination);
              }}
              placeholder="Select a destination"
            >
              {destinations?.map((destination) => (
                <MenuItem
                  data-cy={`${destination?.name}-destination`}
                  key={destination?.name}
                  value={destination?.name}
                >
                  {destination?.name}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        )}
        <Grid item xs={3}>
          <div className={classes.gridContainerExpand}>
            <span className="mr-1">
              {expandAll ? translate("closeAllPanels") : translate("expandAllPanels")}
            </span>
            <IconButton
              onClick={() => {
                setExpandAll(!expandAll);
                if (idSelected !== "" && expandAll) setRoomIdSelected("");
              }}
            >
              {expandAll ? (
                <HiChevronDoubleUp color="#475467" />
              ) : (
                <HiOutlineChevronDoubleDown color="#475467" />
              )}
            </IconButton>
          </div>
        </Grid>
      </Grid>
    );
  };

  return (
    <div>
      {onCustomerView ? null : renderHeaderRoom()}

      <div className="mt-3" data-cy="roomList">
        {data.map((room: RoomJob, index: number) => (
          <RoomContentItem
            locations={locations}
            key={index}
            room={room}
            onCustomerView={onCustomerView}
            destinations={destinations}
            isAllExpanded={expandAll || onCustomerView}
            viewOnly={viewOnly}
            handleDeleteRoom={() => {
              onDeleteRoom(room.name);
              setRoomIdSelected("");
            }}
            selectRoom={(value: boolean) => {
              if (!noHideOnExpand) setExpandAll(value);
              if (value) setRoomIdSelected(room.name);
              else setRoomIdSelected("");
            }}
            updateRoom={(value: string) => handleUpdateRoom(room.name, value, index)}
            update={(value: QuotedItem, itemIndex: number) =>
              handleUpdateItem(room.name, itemIndex, value)
            }
            handleDeleteItem={(value: number) => handleDeleteItem(room.name, value)}
            handleDeleteMaterial={(value: number) => handleDeleteMaterial(room.name, value)}
            updateMaterial={(value: Material, materialIndex: number) =>
              handleUpdateMaterial(room.name, materialIndex, value)
            }
            handleAddPhoto={(pictures: string[]) => handleAddPicturesToRoom(room.name, pictures)}
            handleDeletePhoto={(pictureString?: string) =>
              handleDeletePicturesFromRoom(room.name, pictureString)
            }
          />
        ))}
      </div>
    </div>
  );
};

export default RoomInfoListSection;
